import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Paper,
  Typography,
  Avatar,
  Grid,
  List,
  ListItemText,
  ListItemAvatar,
  ListItem,
  ListItemSecondaryAction,
  useMediaQuery,
  Box,
} from "@material-ui/core";
import {
  USER_PROP,
  USER_COLUMNS_ENHANCED,
  UserRoleText,
  UserRole,
} from "../constants";
import userActions from "../actions";
import TableActions from "./TableActions";
import useStyles from "./UserTableStyles";
import EnhancedTableHead from "components/common/EnhancedTableHead";

const UserTable = () => {
  const classes = useStyles();
  const { setEditUser, loadUsers, setOrderBy, setPagination } = userActions();
  const { members, table, total } = useSelector((state) => state.user);
  const { role } = useSelector((state) => state.auth);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, table]);

  const handleRequestSort = (event, property) => {
    const isAsc = table.orderBy.order === "asc";
    setOrderBy({ col: property, order: isAsc ? "desc" : "asc" });
  };

  const handleChangePage = (event, newPage) => {
    setPagination({
      limit: table.limit,
      offset: newPage * table.limit,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ limit: parseInt(event.target.value, 10), offset: 0 });
  };

  const headCells = [...USER_COLUMNS_ENHANCED];

  if (role === UserRole.SUPERADMIN) {
    headCells.push({
      id: "actions",
      label: "Actions",
      numeric: true,
      disablePadding: false,
      disableSort: true,
    });
  }
  const rowsPerPage = table.limit;

  const getAvatar = (row) => {
    return (
      <Box mr={1}>
        <Avatar className={classes.AvatarSmall}>
          {row[USER_PROP.FNAME][0].toUpperCase() +
            row[USER_PROP.LNAME][0].toUpperCase()}
        </Avatar>
      </Box>
    );
  };
  const matches = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const emptyRows = rowsPerPage - members.length;
  return (
    <Paper className={classes.paper}>
      {matches ? (
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="small"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              columns={headCells}
              classes={classes}
              order={table.orderBy.order}
              orderBy={table.orderBy.col}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {members.map((row) => {
                return (
                  <TableRow
                    onClick={() =>
                      setEditUser({
                        ...row,
                      })
                    }
                    tabIndex={-1}
                    key={row.id}
                  >
                    <TableCell scope="row">
                      <Grid container alignItems="center" direction="row">
                        {getAvatar(row)}
                        <Typography>
                          {`${row[USER_PROP.FNAME]} ${row[USER_PROP.LNAME]}`}
                        </Typography>
                      </Grid>
                    </TableCell>
                    <TableCell>{row[USER_PROP.EMAIL]}</TableCell>
                    <TableCell>{UserRoleText[row[USER_PROP.ROLE]]}</TableCell>
                    {role === UserRole.SUPERADMIN && (
                      <TableCell align="right">
                        <TableActions />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 48 * emptyRows,
                  }}
                >
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <List>
          {members.map((row) => {
            return (
              <ListItem key={row.id}>
                <ListItemAvatar>{getAvatar(row)}</ListItemAvatar>
                <ListItemText
                  primary={`${row[USER_PROP.FNAME]} ${row[USER_PROP.LNAME]}`}
                  secondary={`${row[USER_PROP.EMAIL]} • ${
                    UserRoleText[row[USER_PROP.ROLE]]
                  }`}
                />
                {role === UserRole.SUPERADMIN && (
                  <ListItemSecondaryAction>
                    <Box
                      edge="end"
                      onClick={() =>
                        setEditUser({
                          ...row,
                        })
                      }
                    >
                      <TableActions />
                    </Box>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
      <TablePagination
        rowsPerPageOptions={matches ? [10, 20, 30] : [10]}
        component="div"
        count={total}
        rowsPerPage={table.limit}
        page={table.offset / table.limit}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default UserTable;
